import { v4 as uuidv4 } from 'uuid'

import {
  fetchConfigDataThunk,
  fetchEdgeDataThunk,
  makeFetchSlice,
} from 'app/redux-fetch'
import { createObjectURL } from 'app/utils/createObjectURL'
import { prepareUploadJobImageParams, resizeImage } from 'app/utils/image'
import { sleep } from 'app/utils/sleep'
import { AppDispatch } from 'app/store'
import {
  JOB_INFO_IMAGES_REDUCER_NAME,
  JOB_INFO_REDUCER_NAME,
} from 'features/jobs/consts'
import { fetchDomains } from 'features/domains/api'
import {
  addHome,
  deleteJob,
  deleteJobImage,
  fetchJobImage,
  fetchJobImages,
  newDomain,
  newJob,
  NewJobParams,
  updateJob,
  uploadJobImage,
} from './api'
import type {
  AddHomeParams,
  DeleteJobParams,
  NewDomainParams,
  UpdateJobParams,
  JobImageParams,
  UploadJobImageParams,
} from './api'

export const addHomeThunk = (params: AddHomeParams) => {
  return async (dispatch: AppDispatch) => {
    return await fetchEdgeDataThunk({
      dispatch,
      asyncFunc: () => addHome(params),
    })
  }
}

export const newDomainThunk = (params: NewDomainParams) => {
  return async (dispatch: AppDispatch) => {
    return await fetchEdgeDataThunk({
      dispatch,
      asyncFunc: () => newDomain(params),
    })
  }
}

const fetchJobInfoSlice = makeFetchSlice(JOB_INFO_REDUCER_NAME)
export const fetchJobInfoReducer = fetchJobInfoSlice.reducer
export const fetchJobInfoActions = fetchJobInfoSlice.actions

export const newJobThunk = (params: NewJobParams) => {
  return async (dispatch: AppDispatch) => {
    return await fetchEdgeDataThunk({
      dispatch,
      asyncFunc: () => newJob(params),
    })
  }
}

export const fetchJobInfoThunk = (jobId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchEdgeDataThunk({
        dispatch,
        actions: fetchJobInfoActions,
        asyncFunc: () =>
          fetchDomains({ domainid: jobId, homes: true, jobs: false }),
      })
    } catch (error) {
      console.log('Error fetching job info data', error)
    }
  }
}

export const updateJobThunk = (params: UpdateJobParams) => {
  return async (dispatch: AppDispatch) => {
    return await fetchEdgeDataThunk({
      dispatch,
      asyncFunc: () => updateJob(params),
    })
  }
}

export const deleteJobThunk = (params: DeleteJobParams) => {
  return async (dispatch: AppDispatch) => {
    return await fetchEdgeDataThunk({
      dispatch,
      asyncFunc: () => deleteJob(params),
    })
  }
}

const fetchJobImagesSlice = makeFetchSlice(JOB_INFO_IMAGES_REDUCER_NAME)
export const jobImagesReducer = fetchJobImagesSlice.reducer
export const jobImagesActions = fetchJobImagesSlice.actions

export const fetchJobImagesThunk = (jobId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchEdgeDataThunk({
        dispatch,
        actions: jobImagesActions,
        asyncFunc: () => fetchJobImages(jobId),
      })
    } catch (error) {
      console.log('Error fetching job images data', error)
    }
  }
}

export const fetchJobImageThunk = ({ jobId, fileName }: JobImageParams) => {
  return async (dispatch: AppDispatch) => {
    const image = await fetchConfigDataThunk({
      dispatch,
      asyncFunc: () => fetchJobImage({ jobId, fileName }),
    })

    return createObjectURL(image)
  }
}

export const deleteJobImageThunk = ({ jobId, fileName }: JobImageParams) => {
  return async (dispatch: AppDispatch) => {
    return await fetchEdgeDataThunk({
      dispatch,
      asyncFunc: () => deleteJobImage({ jobId, fileName }),
    })
  }
}

export const uploadJobImageThunk = ({ file, params }: UploadJobImageParams) => {
  return async (dispatch: AppDispatch) => {
    return await fetchEdgeDataThunk({
      dispatch,
      asyncFunc: () => uploadJobImage({ file, params }),
    })
  }
}
export interface uploadJobImageFullAndThumbProps {
  imageFull: File
  jobId: string
}

export const uploadJobImageFullAndThumbThunk = ({
  imageFull,
  jobId,
}: uploadJobImageFullAndThumbProps) => {
  return async (dispatch: AppDispatch) => {
    const fileSetId = uuidv4()
    const imageFullParams = prepareUploadJobImageParams({
      jobId,
      imageFile: imageFull,
      fileSetId,
      isThumbnail: false,
    })

    const imageThumb = await resizeImage(imageFull, 300)
    const imageThumbParams = prepareUploadJobImageParams({
      jobId,
      imageFile: imageThumb,
      fileSetId,
      isThumbnail: true,
    })

    await dispatch(
      uploadJobImageThunk({ file: imageFull, params: imageFullParams })
    )
    await dispatch(
      uploadJobImageThunk({ file: imageThumb, params: imageThumbParams })
    )

    // backend issue - it needs ~3 sec to update images data(info taken from old SCM)
    await sleep(3000)
    await dispatch(fetchJobImagesThunk(jobId))
  }
}
